import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { type Area } from 'react-easy-crop'
import { showErrorData, showSuccessData } from '@/lib/toast'
import {
  usePostUploadAvatar,
  usePutUsersProfile,
} from '@/types/origin-point-engine'
import {
  cropToCircularAvatarBlob,
  extractAvatarUrl,
  validateAvatarFile,
} from '@/features/settings/lib/avatar-uploader-utils'

type UseAvatarUploaderOptions = {
  initialUrl?: string | null
  onUploaded?: (url: string) => void
}

export function useAvatarUploader({
  initialUrl,
  onUploaded,
}: UseAvatarUploaderOptions) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialUrl ?? null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [processing, setProcessing] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const uploadMutation = usePostUploadAvatar()
  const updateProfileMutation = usePutUsersProfile()

  const busy =
    uploadMutation.isPending || updateProfileMutation.isPending || processing
  const canConfirm = Boolean(previewUrl && croppedAreaPixels && !busy)

  useEffect(() => {
    setAvatarUrl(initialUrl ?? null)
  }, [initialUrl])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  useEffect(() => {
    if (!dialogOpen) return
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
  }, [dialogOpen])

  const uploadButtonText = useMemo(() => {
    if (uploadMutation.isPending) return '上传中...'
    if (updateProfileMutation.isPending) return '更新中...'
    if (processing) return '处理中...'
    return '上传头像'
  }, [processing, updateProfileMutation.isPending, uploadMutation.isPending])

  function openFilePicker() {
    if (busy) return
    inputRef.current?.click()
  }

  function closeDialog() {
    if (busy) return
    setDialogOpen(false)
    setPreviewUrl(null)
  }

  function handleDialogChange(nextOpen: boolean) {
    if (busy) return
    if (!nextOpen) {
      closeDialog()
      return
    }
    setDialogOpen(true)
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    const validation = validateAvatarFile(file)
    if (!validation.valid) {
      showErrorData(validation.message ?? '文件校验失败')
      return
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl)
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    setDialogOpen(true)
  }

  function handleCropComplete(_: Area, areaPixels: Area) {
    setCroppedAreaPixels(areaPixels)
  }

  async function handleConfirm() {
    if (!previewUrl || !croppedAreaPixels || busy) return

    try {
      setProcessing(true)
      const { blob, mimeType } = await cropToCircularAvatarBlob({
        imageUrl: previewUrl,
        croppedAreaPixels,
      })

      const file = new File([blob], 'avatar.jpg', { type: mimeType })
      const response = await uploadMutation.mutateAsync({
        data: { avatar: file },
      })
      const url = extractAvatarUrl(response)

      if (!url) {
        throw new Error('未返回头像地址')
      }

      await updateProfileMutation.mutateAsync({ data: { avatarUrl: url } })

      setAvatarUrl(url)
      setDialogOpen(false)
      setPreviewUrl(null)
      showSuccessData('头像上传成功')
      onUploaded?.(url)
    } catch (error) {
      showErrorData('头像上传失败', error)
    } finally {
      setProcessing(false)
    }
  }

  return {
    avatarUrl,
    dialogOpen,
    previewUrl,
    crop,
    zoom,
    busy,
    canConfirm,
    uploadButtonText,
    inputRef,
    setCrop,
    setZoom,
    openFilePicker,
    closeDialog,
    handleDialogChange,
    handleFileChange,
    handleConfirm,
    handleCropComplete,
  }
}
